import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Alert } from 'antd';
import * as SolidIcons from '@heroicons/react/solid';
import './SpecAlert.css';

export interface SpecAlertProps {
  message: string | null;
  duration?: number;
  onDone?: () => void;
}

const SpecAlert: React.FC<SpecAlertProps> = ({ message, duration = 2500, onDone }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [currentMessage, setCurrentMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  }, []);

  useEffect(() => {
    if (message) {
      cleanup();
      setCurrentMessage(message);
      setExiting(false);
      setVisible(true);

      timerRef.current = setTimeout(() => {
        setExiting(true);
        exitTimerRef.current = setTimeout(() => {
          setVisible(false);
          setExiting(false);
          setCurrentMessage(null);
          onDone?.();
        }, 250);
      }, duration);
    }
  }, [message, duration, onDone, cleanup]);

  useEffect(() => cleanup, [cleanup]);

  if (!visible || !currentMessage) return null;

  return createPortal(
    <div className="spec-alert-portal">
      <Alert
        className={exiting ? 'spec-alert--exiting' : undefined}
        message={currentMessage}
        type="info"
        showIcon
        icon={<SolidIcons.CheckCircleIcon />}
      />
    </div>,
    document.body
  );
};

export default SpecAlert;
